import * as React from 'react';
import { apiFetch } from '../lib/apiClient';

/** The slice of workspace-asset metadata the UI needs to label / preview a file. */
export interface WorkspaceAssetMeta {
  uuid: string;
  originalFileName?: string;
  mimeType?: string;
}

/**
 * Fetches a workspace asset's metadata (name, mime type) by uuid so a caller can
 * label an uploaded file or switch image-vs-document rendering. Returns null while
 * loading or if the fetch fails (best-effort: callers fall back to a generic
 * label rather than erroring).
 */
export function useWorkspaceAssetMeta(uuid: string | null | undefined): WorkspaceAssetMeta | null {
  const [meta, setMeta] = React.useState<WorkspaceAssetMeta | null>(null);

  React.useEffect(() => {
    if (!uuid) {
      setMeta(null);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const result = await apiFetch<{ asset: WorkspaceAssetMeta | null }>(
          `/api/workspace-assets/${uuid}`
        );
        if (!cancelled) setMeta(result.asset ?? null);
      } catch {
        // best-effort - leave null and let the caller show a generic label
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [uuid]);

  return meta;
}
